import { Component, OnInit, inject } from '@angular/core';
import { RouterLink, Router, ActivatedRoute, Params } from '@angular/router';
import { AuthService } from '../../../../services/auth.service';
import { CommonModule } from '@angular/common';
import { ApiService } from '../../../../services/api.service';
import { ToastrService } from 'ngx-toastr';
import { ShortenPipe } from '../../../../pipes/shorten.pipe';
import * as _ from 'lodash';
import { FormsModule } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { Ad, AdMetrics } from '../../../../interfaces/performance-insights';
import { PerformanceInsightsService } from '../../../../services/performance-insights.service';
import { AdAccountService } from '../../../../services/ad-account.service';
import { NgbDatepickerModule, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-performance-insights-ads',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    FormsModule,
    ShortenPipe,
    NgbTooltipModule,
    NgbDatepickerModule
  ],
  templateUrl: './performance-insights-ads.component.html',
  styleUrl: './performance-insights-ads.component.css'
})
export class PerformanceInsightsAdsComponent implements OnInit {
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  router: Router = inject(Router);
  authService: AuthService = inject(AuthService);
  apiService: ApiService = inject(ApiService);
  toastrService: ToastrService = inject(ToastrService);
  performanceInsightsService: PerformanceInsightsService = inject(PerformanceInsightsService);
  AdAccountService: AdAccountService = inject(AdAccountService);

  // User Authentication
  authenticatedUser = this.authService.authenticatedUser;

  // Component-specific variables
  accountId: string = '';
  accountMetaId: string = '';


  views = {
    ads: {
      title: 'All Ads',
      description: 'View all ads in the ad account',
      show: true,
      ads: [] as Ad[],
      loadingAds: true,
      orderBy: 'roas',
      orderDir: 'desc',
      dateStart: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days ago
      dateEnd: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Yesterday
      filterProductCode: '',
      filterAdNameContains: null,
      filterAdNameContains2: null
    },
    adsPerDay: {
      title: 'Daily Performance',
      description: 'View daily ad performance',
      show: false,
      adsPerDay: [] as {date: string, metrics: AdMetrics}[],
      loadingAdsPerDay: false,
      orderBy: 'date',
      orderDir: 'desc',
      dateStart: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days ago
      dateEnd: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // Yesterday
    },
    adsCreativeTest: {
      title: 'Creative Test',
      description: 'View ads performance for a Creative Test',
      show: false,
      ads: [] as Ad[],
      loadingAds: true,
      orderBy: 'roas',
      orderDir: 'desc',
      dateStart: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days ago
      dateEnd: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Yesterday
      filterAdNameContains: 'CTESTING',
      filterAdNameContains2: null
    }
} 


  ngOnInit(): void {
    
    // Check that user is authenticated (should be the case if this path is protected with AuthGuardService in app.routes.ts)
    this.authService.checkUserSignedIn().then(checkSign => {
      if (!checkSign) {
        // User is NOT signed in => Call AuthService.signOut() to redirect to Sign in page
        this.authService.signOut();
      } else {
        // User is signed in
        // Get Ad Account ID from URL params
        this.activatedRoute.params.subscribe((params: Params) => {
          this.accountId = params['id'];
          console.log(`Loading Ad Account#: ${this.accountId}`);

          // Show the ads view by default
          this.showViewAdsPerDay();          

          // Check if the user has access to the Ad Accounts section & this specific ad account
          this.authService.checkUserHasAccessToAdAccounts().then(adAccounts => {
            console.log('adAccounts', adAccounts);
            if(adAccounts === null || !adAccounts.some(adAccount => adAccount.id === this.accountId)) {
              // User does not have access to the account (or the user has no ad accounts at all)
              console.log("User does not have access to this ad account");
              this.toastrService.error("You do not have access to this Ad Account", "Access Denied");
              this.router.navigate(['/']);
            } else {
              // User has access to the account
              console.log("User has access to this ad account");

              // Retrieve ad account Meta Id
              this.AdAccountService.getAdAccount(this.accountId)
              .then(adAccount => {
                console.log('adAccount', adAccount);
                if(adAccount && adAccount.meta_id) {
                  this.accountMetaId = adAccount.meta_id;
                }
              })

            }        
          });
        });
      }  
    });
  }


  // Get all ads
  async getAds() {
      return new Promise<Ad[]>((resolve, reject) => {
          console.log("getAds()");
          let ads: Ad[] = [];
          this.views.ads.loadingAds = true;
          let optionsGetAds: { limit: number; date_start: string; date_end: string; order_by: string; order_dir: string; product_code?: string; ad_name_contains?: string; ad_name_contains_2?: string } = { limit: 50, date_start: this.views.ads.dateStart, date_end: this.views.ads.dateEnd, order_by: this.views.ads.orderBy, order_dir: this.views.ads.orderDir };
          if(this.views.ads.filterProductCode) {
            optionsGetAds.product_code = this.views.ads.filterProductCode;
          }
          if(this.views.ads.filterAdNameContains) {
            optionsGetAds.ad_name_contains = this.views.ads.filterAdNameContains;
          }
          if(this.views.ads.filterAdNameContains2) {
            optionsGetAds.ad_name_contains_2 = this.views.ads.filterAdNameContains2;
          }
          this.performanceInsightsService.getAds(this.accountId, optionsGetAds)
          .then(response => {
              console.log("[PerformanceInsightsComponent] getAds() API response", response);
              // Get ads from response
              if (response && response.length > 0) {
                  ads = response.map((ad: any) => {
                      return {
                          id: ad.id,
                          name: ad.name,
                          adset_id: ad.adset_id,
                          adset_name: ad.adset_name,
                          campaign_id: ad.campaign_id,
                          campaign_name: ad.campaign_name,
                          creative_image_url: ad.creative_image_url,
                          metrics: ad.metrics
                      };
                  });
              } else {
                  console.log("[PerformanceInsightsComponent] getAds() No ads found");
              }
              this.views.ads.loadingAds = false;
              resolve(ads);
          })
          .catch(error => {
              console.log("[PerformanceInsightsComponent] getAds() API error", error);
              this.views.ads.loadingAds = false;
              reject(error.name);
          });

      });
  }  

  // Get ads per day
  async getAdsPerDay() {
    return new Promise<{date: string, metrics: AdMetrics}[]>((resolve, reject) => {
      console.log("getAdsPerDay()");
      let adsPerDay: {date: string, metrics: AdMetrics}[] = [];
      this.views.adsPerDay.loadingAdsPerDay = true;
      this.performanceInsightsService.getAdsPerDay(this.accountId, { date_start: this.views.adsPerDay.dateStart, date_end: this.views.adsPerDay.dateEnd, order_by: this.views.adsPerDay.orderBy, order_dir: this.views.adsPerDay.orderDir })
      .then(response => {
        console.log("[PerformanceInsightsComponent] getAdsPerDay() API response", response);
        // Get ads from response
        if (response && response.length > 0) {
          adsPerDay = response.map((day: any) => {
            return {
              date: day.date,
              metrics: day.metrics
            };
          });
        } else {
          console.log("[PerformanceInsightsComponent] getAdsPerDay() No ads found");
        }
        this.views.adsPerDay.loadingAdsPerDay = false;
        resolve(adsPerDay);
      })
      .catch(error => {
        console.log("[PerformanceInsightsComponent] getAdsPerDay() API error", error);
        this.views.adsPerDay.loadingAdsPerDay = false;
        reject(error.name);
      });

    });
  }

  // Get Ads Creative Test
  async getAdsCreativeTest() {
      return new Promise<Ad[]>((resolve, reject) => {
          console.log("getAdsCreativeTest()");
          let ads: Ad[] = [];
          this.views.adsCreativeTest.loadingAds = true;
          let optionsGetAds: { limit: number; date_start: string; date_end: string; order_by: string; order_dir: string; ad_name_contains?: string; ad_name_contains_2?: string } = { limit: 50, date_start: this.views.adsCreativeTest.dateStart, date_end: this.views.adsCreativeTest.dateEnd, order_by: this.views.adsCreativeTest.orderBy, order_dir: this.views.adsCreativeTest.orderDir };
          if(this.views.adsCreativeTest.filterAdNameContains) {
            optionsGetAds.ad_name_contains = this.views.adsCreativeTest.filterAdNameContains;
          }
          if(this.views.adsCreativeTest.filterAdNameContains2) {
            optionsGetAds.ad_name_contains_2 = this.views.adsCreativeTest.filterAdNameContains2;
          }
          this.performanceInsightsService.getAds(this.accountId, optionsGetAds)
          .then(response => {
              console.log("[PerformanceInsightsComponent] getAdsCreativeTest() API response", response);
              // Get ads from response
              if (response && response.length > 0) {
                  ads = response.map((ad: any) => {
                      return {
                          id: ad.id,
                          name: ad.name,
                          adset_id: ad.adset_id,
                          adset_name: ad.adset_name,
                          campaign_id: ad.campaign_id,
                          campaign_name: ad.campaign_name,
                          creative_image_url: ad.creative_image_url,
                          metrics: ad.metrics
                      };
                  });
              } else {
                  console.log("[PerformanceInsightsComponent] getAdsCreativeTest() No ads found");
              }
              this.views.adsCreativeTest.loadingAds = false;
              resolve(ads);
          })
          .catch(error => {
              console.log("[PerformanceInsightsComponent] getAdsCreativeTest() API error", error);
              this.views.adsCreativeTest.loadingAds = false;
              reject(error.name);
          });

      });
  }  


  changeOrderBy(view: string, orderBy: string) {
    console.log("changeOrderBy()", view, orderBy);
    if(view === 'ads') {
      this.views.ads.orderBy = orderBy;
      this.views.ads.orderDir = this.views.ads.orderDir === 'asc' ? 'desc' : 'asc';
      this.views.ads.ads = [];
      this.getAds().then(ads => {
        console.log("changeOrderBy Ads", ads);
        this.views.ads.ads = ads;
      });
    } else if(view === 'adsPerDay') {
      this.views.adsPerDay.orderBy = orderBy;
      this.views.adsPerDay.orderDir = this.views.adsPerDay.orderDir === 'asc' ? 'desc' : 'asc';
      this.views.adsPerDay.adsPerDay = [];
      this.getAdsPerDay().then(adsPerDay => {
        console.log("changeOrderBy Ads per day", adsPerDay);
        this.views.adsPerDay.adsPerDay = adsPerDay;
      });      
    } else if(view === 'adsCreativeTest') {
      this.views.adsCreativeTest.orderBy = orderBy;
      this.views.adsCreativeTest.orderDir = this.views.adsCreativeTest.orderDir === 'asc' ? 'desc' : 'asc';
      this.views.adsCreativeTest.ads = [];
      this.getAdsCreativeTest().then(ads => {
        console.log("changeOrderBy Ads", ads);
        this.views.adsCreativeTest.ads = ads;
      });
    }

  }

  showViewAds() {
    this.views.ads.show = true;
    this.views.adsPerDay.show = false;
    this.views.adsCreativeTest.show = false;
    this.views.ads.ads = [];
    this.getAds().then(ads => {
      console.log("Ads", ads);
      this.views.ads.ads = ads;
    });
  }

  showViewAdsPerDay() {
    this.views.ads.show = false;
    this.views.adsPerDay.show = true;
    this.views.adsCreativeTest.show = false;
    this.views.adsPerDay.adsPerDay = [];
    this.getAdsPerDay().then(adsPerDay => {
      console.log("Ads per day", adsPerDay);
      this.views.adsPerDay.adsPerDay = adsPerDay;
    });
  }

  showViewAdsCreativeTest() {
    this.views.ads.show = false;
    this.views.adsPerDay.show = false;
    this.views.adsCreativeTest.show = true;
    this.views.adsCreativeTest.ads = [];
    this.getAdsCreativeTest().then(ads => {
      console.log("Ads Creative Test", ads);
      this.views.adsCreativeTest.ads = ads;
    });
  }

  changeView(view: string) {
    console.log("changeView()", view);
    if(view === 'ads') {
      this.showViewAds();
    } else if(view === 'adsPerDay') {
      this.showViewAdsPerDay();
    } else if(view === 'adsCreativeTest') {
      this.showViewAdsCreativeTest();
    }
  }

  changeDate(view: string, date: string, ngbDate: NgbDateStruct) {
    console.log("changeDate()", view, date, ngbDate);
    const year = ngbDate.year;
    const month = String(ngbDate.month).padStart(2, '0');
    const day = String(ngbDate.day).padStart(2, '0');
    let newDate = `${year}-${month}-${day}`;
    switch(view) {
      case 'ads':
        console.log("Change date for ads", date, newDate);
        if(date === 'start') {
          this.views.ads.dateStart = newDate;
        } else if(date === 'end') {
          this.views.ads.dateEnd = newDate;
        } else {
          this.toastrService.error("Invalid date type", "Error");
          return;
        }
        this.views.ads.ads = [];
        this.getAds().then(ads => {
          console.log("Ads", ads);
          this.views.ads.ads = ads;
        });
        break;
      case 'adsPerDay':
        console.log("Change date for ads per day", date, newDate);
        if(date === 'start') {
          this.views.adsPerDay.dateStart = newDate;
        } else if(date === 'end') {
          this.views.adsPerDay.dateEnd = newDate;
        } else {
          this.toastrService.error("Invalid date type", "Error");
          return;
        }
        this.views.adsPerDay.adsPerDay = [];
        this.getAdsPerDay().then(adsPerDay => {
          console.log("adsPerDay", adsPerDay);
          this.views.adsPerDay.adsPerDay = adsPerDay;
        });
        break;
      case 'adsCreativeTest':
        console.log("Change date for adsCreativeTest", date, newDate);
        if(date === 'start') {
          this.views.adsCreativeTest.dateStart = newDate;
        } else if(date === 'end') {
          this.views.adsCreativeTest.dateEnd = newDate;
        } else {
          this.toastrService.error("Invalid date type", "Error");
          return;
        }
        this.views.adsCreativeTest.ads = [];
        this.getAdsCreativeTest().then(ads => {
          console.log("adsCreativeTest", ads);
          this.views.adsCreativeTest.ads = ads;
        });
        break;
    }
    
  }

  changeFilter(view: string, filter: string, value: any) {
    console.log("changeFilter()", view, filter, value);
    if(view === 'ads') {
      if(filter === 'product_code') this.views.ads.filterProductCode = value;
      if(filter === 'ad_name_contains') this.views.ads.filterAdNameContains = value;
      if(filter === 'ad_name_contains_2') this.views.ads.filterAdNameContains2 = value;
      this.views.ads.ads = [];
      this.getAds().then(ads => {
        console.log("Ads", ads);
        this.views.ads.ads = ads;
      });
    } else if(view === 'adsCreativeTest') {
      if(filter === 'ad_name_contains') this.views.adsCreativeTest.filterAdNameContains = value;
      if(filter === 'ad_name_contains_2') this.views.adsCreativeTest.filterAdNameContains2 = value;
      this.views.adsCreativeTest.ads = [];
      this.getAdsCreativeTest().then(ads => {
        console.log("adsCreativeTest", ads);
        this.views.adsCreativeTest.ads = ads;
      });
    }
  }


}
